import {
  useQuery,
  useMutation,
  useQueryClient,
  skipToken,
} from '@tanstack/react-query';
import { signalService } from '../services/signal.service';
import type {
  CreateSignalApiRequest,
  UpdateSignalApiRequest,
  GetSignalsApiRequest,
  SignalApplicabilityScope,
} from '@momentum/types';

export type SignalFilters = GetSignalsApiRequest;

/**
 * Query key factory for signals
 * Follows React Query best practices for cache management
 */
export const signalsKeys = {
  all: ['signals'] as const,
  lists: () => [...signalsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...signalsKeys.lists(), filters] as const,
  details: () => [...signalsKeys.all, 'detail'] as const,
  detail: (id: string) => [...signalsKeys.details(), id] as const,
  relatedIssues: (id: string) =>
    [...signalsKeys.all, 'related-issues', id] as const,
  marketplace: () => [...signalsKeys.all, 'marketplace'] as const,
};

/**
 * Hook to fetch all signals with optional filters
 */
export const useSignals = (
  filters?: SignalFilters,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: signalsKeys.list((filters || {}) as Record<string, unknown>),
    queryFn: () => signalService.getSignals(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled !== false,
  });
};

/**
 * Hook to fetch a single signal by ID
 */
export const useSignal = (id: string) => {
  return useQuery({
    queryKey: signalsKeys.detail(id),
    queryFn: () => signalService.getSignalById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch multiple signals by IDs (single batch request)
 */
export const useSignalsByIds = (
  signalIds: string[],
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [...signalsKeys.lists(), 'by-ids', signalIds] as const,
    queryFn: () => signalService.getSignalsByIds(signalIds),
    enabled: options?.enabled !== false && signalIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a new signal
 */
export const useCreateSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSignalApiRequest) =>
      signalService.createSignal(data),
    onSuccess: () => {
      // Invalidate all signal lists to refresh the UI
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a signal
 */
export const useDeleteSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => signalService.deleteSignal(id),
    onSuccess: () => {
      // Invalidate all signal lists to refresh the UI
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
    },
  });
};

/**
 * Hook to update a signal (owner only)
 */
export const useUpdateSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSignalApiRequest }) =>
      signalService.updateSignal(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: signalsKeys.detail(variables.id),
      });
    },
  });
};

/**
 * Hook to activate a signal
 */
export const useActivateSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      scope,
    }: {
      id: string;
      scope?: SignalApplicabilityScope;
    }) => signalService.activateSignal(id, scope),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: signalsKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: signalsKeys.marketplace(),
      });
    },
  });
};

/**
 * Hook to deactivate a signal
 */
export const useDeactivateSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => signalService.deactivateSignal(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: signalsKeys.marketplace() });
    },
  });
};

/**
 * Hook to fetch marketplace signals (all accessible signals)
 */
export const useMarketplaceSignals = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: signalsKeys.marketplace(),
    queryFn: () => signalService.getMarketplaceSignals(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  });
};

/**
 * Hook to import a signal
 */
export const useImportSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => signalService.importSignal(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: signalsKeys.marketplace() });
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
    },
  });
};

/**
 * Hook to remove an imported marketplace signal
 */
export const useRemoveImportedSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => signalService.removeImportedSignal(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: signalsKeys.marketplace() });
      queryClient.invalidateQueries({ queryKey: signalsKeys.lists() });
    },
  });
};

/**
 * Hook to fetch related issues for a signal
 */
export const useSignalRelatedIssues = (
  signalId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const isEnabled = !!signalId && options?.enabled !== false;

  return useQuery({
    queryKey: signalsKeys.relatedIssues(signalId ?? ''),
    queryFn: isEnabled
      ? () => signalService.getRelatedIssues(signalId)
      : skipToken,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to apply a signal to subordinates
 */
export const useApplySignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      scope,
    }: {
      id: string;
      scope: SignalApplicabilityScope;
    }) => signalService.applySignal(id, scope),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: signalsKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: signalsKeys.marketplace(),
      });
    },
  });
};

/**
 * Hook to change the applicability scope of a signal (single request)
 */
export const useChangeSignalScope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      newScope,
    }: {
      id: string;
      newScope: SignalApplicabilityScope;
    }) => signalService.changeSignalScope(id, newScope),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: signalsKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: signalsKeys.marketplace(),
      });
    },
  });
};

/**
 * Hook to unapply a signal from subordinates
 */
export const useUnapplySignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      scope,
    }: {
      id: string;
      scope: SignalApplicabilityScope;
    }) => signalService.unapplySignal(id, scope),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: signalsKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: signalsKeys.marketplace(),
      });
    },
  });
};

/**
 * Hook to generate signal suggestions from uploaded Word files
 */
export const useGenerateSignalsFromFile = () => {
  return useMutation({
    mutationFn: (files: File[]) => signalService.generateSignalsFromFile(files),
  });
};
